import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("Nulstillingslinket er ugyldigt.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Adgangskoden skal være mindst 8 tegn.");
    if (password !== confirm) return setErr("Adgangskoderne matcher ikke.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center animate-fade-up" data-testid="reset-done-page">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-olive-100 text-olive-600 mx-auto">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="mt-6 font-serif text-3xl text-olive-700">Din adgangskode er opdateret</h1>
        <p className="mt-3 text-brown-600 text-sm">
          Viderestiller til log ind…
        </p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center animate-fade-up" data-testid="reset-invalid-page">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive mx-auto">
          <AlertCircle className="h-8 w-8" />
        </div>
        <h1 className="mt-6 font-serif text-3xl text-olive-700">Ugyldigt link</h1>
        <p className="mt-3 text-brown-600 text-sm">
          Nulstillingslinket mangler et gyldigt token. Bed om et nyt link nedenfor.
        </p>
        <Link to="/forgot-password" className="btn-primary mt-8 inline-flex" data-testid="go-forgot">
          Bed om nyt link
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16 animate-fade-up" data-testid="reset-password-page">
      <div className="eyebrow">Nulstil adgangskode</div>
      <h1 className="mt-3 font-serif text-4xl text-olive-700">Vælg en ny adgangskode</h1>
      <p className="mt-3 text-brown-600 text-sm">
        Mindst 8 tegn. Brug gerne en sætning som er nem for dig at huske.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm mb-1 text-brown-600">Ny adgangskode</label>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field"
            placeholder="••••••••"
            data-testid="reset-password-input"
          />
        </div>
        <div>
          <label className="block text-sm mb-1 text-brown-600">Bekræft adgangskode</label>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="field"
            placeholder="••••••••"
            data-testid="reset-password-confirm"
          />
        </div>
        {err && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2" data-testid="reset-error">
            {err}
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="reset-submit">
          <KeyRound className="h-4 w-4" /> {busy ? "Opdaterer…" : "Opdatér adgangskode"}
        </button>
      </form>
    </div>
  );
}
